import React, { useEffect, useState } from 'react';
import Button from './Button';
import CloseCircleIcon from '../icons/CloseCircleIcon';

interface ClassRole {
  id: string;
  name: string;
  description?: string;
  salaryType: string;
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
  salary: number;
  maxCount: number;
  currentCount?: number;
  isActive: boolean;
}

interface AssignedRole {
  roleId: string;
  assignedAt: Date;
  isActive: boolean;
}

interface StudentRoleAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  studentName: string;
  currentRoles: AssignedRole[];
  availableRoles: ClassRole[];
  maxRolesPerStudent?: number;
  onAssignRole: (roleId: string) => void;
  onRemoveRole: (roleId: string) => void;
  scale?: number;
}

const PERIOD_LABEL: Record<string, string> = {
  daily: '매일',
  weekly: '매주',
  monthly: '매월',
};

const StudentRoleAssignmentDialog: React.FC<StudentRoleAssignmentDialogProps> = ({
  isOpen,
  onClose,
  studentName,
  currentRoles,
  availableRoles,
  maxRolesPerStudent = 1,
  onAssignRole,
  onRemoveRole,
  scale = 1,
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedRoleId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const assignedIds = new Set(currentRoles.map((role) => role.roleId));
  const atLimit = currentRoles.length >= maxRolesPerStudent;
  const visibleRoles = availableRoles.filter((role) => role.isActive !== false);

  const handleComplete = () => {
    if (selectedRoleId) {
      onAssignRole(selectedRoleId);
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-family-primary)',
        zIndex: 1000,
        padding: '24px',
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-assign-title"
        style={{
          background: 'var(--Color-background-normal, white)',
          borderRadius: '16px',
          width: 'min(440px, 100%)',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        <div style={{ padding: '24px 24px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
            <div>
              <h2
                id="role-assign-title"
                style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '30px',
                  color: 'var(--Color-text-normal, #161616)',
                }}
              >
                역할 부여
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '14px', lineHeight: '21px', color: 'var(--Color-text-assistive, #989898)' }}>
                <span style={{ color: 'var(--Color-text-brand-color, #FF6F0F)', fontWeight: 600 }}>{studentName}</span>
                {' '}학생 · 최대 {maxRolesPerStudent}개
              </p>
            </div>
            <button
              type="button"
              aria-label="닫기"
              onClick={onClose}
              style={{
                width: '32px',
                height: '32px',
                border: 'none',
                borderRadius: '8px',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src="/images/icons/closeIcon.svg" alt="" style={{ width: '14px', height: '14px' }} />
            </button>
          </div>

          {currentRoles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {currentRoles.map((assignedRole) => {
                const roleInfo = availableRoles.find((role) => role.id === assignedRole.roleId);
                if (!roleInfo) return null;
                return (
                  <button
                    key={assignedRole.roleId}
                    type="button"
                    onClick={() => onRemoveRole(roleInfo.id)}
                    aria-label={`${roleInfo.name} 역할 해제`}
                    style={{
                      padding: '6px 10px',
                      background: 'var(--Color-fill-normal, #293044)',
                      borderRadius: '999px',
                      border: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      color: 'white',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                    }}
                  >
                    {roleInfo.name}
                    <CloseCircleIcon width={12} height={12} color="white" />
                  </button>
                );
              })}
            </div>
          )}

          <div
            role="listbox"
            aria-label="부여할 역할"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: '320px',
              overflowY: 'auto',
            }}
          >
            {visibleRoles.length === 0 && (
              <div style={{ padding: '28px 8px', textAlign: 'center', color: 'var(--Color-text-assistive, #989898)', fontSize: '14px' }}>
                등록된 역할이 없습니다.
              </div>
            )}
            {visibleRoles.map((role) => {
              const count = role.currentCount || 0;
              const isFull = count >= role.maxCount;
              const isAssigned = assignedIds.has(role.id);
              const disabled = isFull || isAssigned || atLimit;
              const selected = selectedRoleId === role.id;
              const salaryUnit = role.salaryType === 'stamp' ? '도장' : 'P';
              const period = PERIOD_LABEL[role.salaryPeriod] || role.salaryPeriod;

              return (
                <button
                  key={role.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={disabled}
                  onClick={() => setSelectedRoleId(role.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: selected
                      ? '1.5px solid var(--Color-fill-brand-color, #FF6F0F)'
                      : '1px solid var(--Color-line-normal, #F0F0F0)',
                    background: selected ? '#fff8f3' : 'var(--Color-background-normal, white)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '15px',
                        fontWeight: 600,
                        lineHeight: '22px',
                        color: 'var(--Color-text-normal, #161616)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {role.name}
                    </span>
                    <span style={{ display: 'block', marginTop: '2px', fontSize: '12px', lineHeight: '18px', color: 'var(--Color-text-assistive, #989898)' }}>
                      {period} · {role.salary}{salaryUnit} · {count}/{role.maxCount}명
                      {isAssigned ? ' · 부여됨' : isFull ? ' · 마감' : ''}
                    </span>
                  </span>
                  <span
                    aria-hidden
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      border: selected
                        ? '5px solid var(--Color-fill-brand-color, #FF6F0F)'
                        : '1.5px solid #D0D0D0',
                      boxSizing: 'border-box',
                    }}
                  />
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="cancel" size="medium" width="88px" onClick={onClose}>
              취소
            </Button>
            <Button
              variant="submit"
              size="medium"
              width="108px"
              disabled={!selectedRoleId}
              onClick={handleComplete}
            >
              부여하기
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRoleAssignmentDialog;
